import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Boxes,
  Database,
  Download,
  Gauge,
  Layers,
  Minus,
  Package,
  PackageCheck,
  Search,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ScoreTile } from './charts/ScoreTile';
import { VelocityBar } from './consumption/VelocityBar';
import {
  ABC_BADGE,
  classifyAbc,
  matchesConsumptionStockKind,
  summarizePreprintedConsumption,
  type ConsumptionRow,
  type ConsumptionStockKind,
} from './consumption/types';
import { useSkuVelocity } from '../hooks/useReportingV2';
import {
  buildConsumptionCsv,
  consumptionExportFilename,
  downloadCsv,
} from '../lib/consumption-export';
import { updateSearchParams } from '@/lib/search-params';
import {
  formatUnits,
  formatRateAdaptive,
  formatPercent,
  formatExactDaysOfCover,
  daysOfCoverBand,
  RAG_TEXT,
} from '../lib/reporting-format';

interface ConsumptionTabProps {
  organizationId: string | null;
  startDate: Date;
  endDate: Date;
  locationId: string | null;
  locationName: string | null;
  /** Only true when this tab is active — gates every query. */
  active: boolean;
}

type SortField =
  | 'sku'
  | 'units'
  | 'velocity'
  | 'trend'
  | 'orders'
  | 'daysOfCover'
  | 'onHand'
  | 'abc';
type SortDir = 'asc' | 'desc';

const ALL_CATEGORIES = '__all__';
const ALL_TYPES = '__all__';
const ALL_TRENDS = '__all__';
const ALL_STOCK: ConsumptionStockKind = 'all';
const PREPRINTED_STOCK: ConsumptionStockKind = 'preprinted';
const OTHER_STOCK: ConsumptionStockKind = 'other';

// Estimated row height (px) for the virtualizer's first paint. Single-line rows
// are ~45px; rows with a second attribute line (~SKU · colour · size) are taller.
// Dynamic measurement (`measureElement`) corrects each row after it mounts, so
// this is only the pre-measure estimate. Slightly over the single-line height to
// bias the first paint toward rendering a full viewport.
const ESTIMATED_ROW_HEIGHT = 53;

const TREND_META = {
  rising: { Icon: TrendingUp, cls: 'text-emerald-600' },
  falling: { Icon: TrendingDown, cls: 'text-red-600' },
  flat: { Icon: Minus, cls: 'text-muted-foreground' },
} as const;

const ABC_RANK = { A: 0, B: 1, C: 2 } as const;

/**
 * Consumption tab — net physical stock consumed from the inventory ledger.
 * Fulfilment decrements, refunds, and sync corrections share one source and
 * scope with on-hand, so velocity and cover remain internally consistent.
 *
 * The table is the star: sticky header, every column sortable, client-side
 * search + category/type/trend filters, and an inline VelocityBar so the hero
 * column reads as a mini bar chart. The complete filtered/sorted set always
 * renders — the rows are virtualized with TanStack Virtual (DESIGN.md §14) so a
 * 15k-SKU catalog scrolls smoothly inside a table-owned viewport.
 *
 * Mirrors `BlankConsumptionReport.tsx` for the table chrome and the sibling
 * reporting tabs (`StockValueTab` / `PerformanceTab`) for props + query gating.
 */
export const ConsumptionTab: React.FC<ConsumptionTabProps> = ({
  organizationId,
  startDate,
  endDate,
  locationId,
  locationName,
  active,
}) => {
  const { t } = useTranslation('inventory');
  const [searchParams, setSearchParams] = useSearchParams();

  const velocity = useSkuVelocity({
    organizationId,
    startDate,
    endDate,
    locationId,
    enabled: active,
  });

  const rows = React.useMemo<ConsumptionRow[]>(
    () => (velocity.data?.rows ?? []) as ConsumptionRow[],
    [velocity.data],
  );

  // The URL is the source of truth for report controls so deep links, browser
  // Back/Forward, and navigation away from the report preserve the exact view.
  const query = searchParams.get('q') ?? '';
  const category = searchParams.get('category') ?? ALL_CATEGORIES;
  const typeFilter = searchParams.get('type') ?? ALL_TYPES;
  const trendParam = searchParams.get('trend');
  const trendFilter =
    trendParam === 'rising' || trendParam === 'falling' || trendParam === 'flat'
      ? trendParam
      : ALL_TRENDS;
  const stockParam = searchParams.get('stock');
  const stockKind: ConsumptionStockKind =
    stockParam === PREPRINTED_STOCK || stockParam === OTHER_STOCK ? stockParam : ALL_STOCK;
  const sortParam = searchParams.get('sort');
  const sortField: SortField =
    sortParam === 'sku' ||
    sortParam === 'units' ||
    sortParam === 'velocity' ||
    sortParam === 'trend' ||
    sortParam === 'orders' ||
    sortParam === 'daysOfCover' ||
    sortParam === 'onHand' ||
    sortParam === 'abc'
      ? sortParam
      : 'velocity';
  const sortDir: SortDir = searchParams.get('dir') === 'asc' ? 'asc' : 'desc';

  const setFilterParam = React.useCallback(
    (key: string, value: string, defaultValue: string) => {
      setSearchParams(
        (previous) => {
          return updateSearchParams(previous, {
            [key]: value === defaultValue ? null : value,
          });
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ABC class is a stable property of the full period set, not the filtered view.
  const abcBySku = React.useMemo(() => classifyAbc(rows), [rows]);

  const categories = React.useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) if (row.category) set.add(row.category);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const types = React.useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) if (row.type) set.add(row.type);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (category !== ALL_CATEGORIES && row.category !== category) return false;
      if (typeFilter !== ALL_TYPES && row.type !== typeFilter) return false;
      if (trendFilter !== ALL_TRENDS && row.trend !== trendFilter) return false;
      if (!matchesConsumptionStockKind(row, stockKind)) return false;
      if (!q) return true;
      return `${row.sku} ${row.type ?? ''} ${row.color ?? ''} ${row.size ?? ''} ${row.category}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, query, category, typeFilter, trendFilter, stockKind]);

  const sorted = React.useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'sku':
          cmp = a.sku.localeCompare(b.sku);
          break;
        case 'units':
          cmp = a.units - b.units;
          break;
        case 'velocity':
          cmp = a.velocity - b.velocity;
          break;
        case 'orders':
          cmp = a.orders - b.orders;
          break;
        case 'onHand':
          cmp = a.onHand - b.onHand;
          break;
        case 'trend': {
          // Sort by momentum: deltaPct, nulls last.
          const av = a.deltaPct ?? Number.NEGATIVE_INFINITY;
          const bv = b.deltaPct ?? Number.NEGATIVE_INFINITY;
          cmp = av - bv;
          break;
        }
        case 'daysOfCover': {
          // Null cover (no velocity) always sorts last regardless of direction.
          if (a.daysOfCover == null && b.daysOfCover == null) return 0;
          if (a.daysOfCover == null) return 1;
          if (b.daysOfCover == null) return -1;
          cmp = a.daysOfCover - b.daysOfCover;
          break;
        }
        case 'abc':
          cmp = ABC_RANK[abcBySku.get(a.sku) ?? 'C'] - ABC_RANK[abcBySku.get(b.sku) ?? 'C'];
          break;
      }
      return cmp * dir;
    });
  }, [filtered, sortField, sortDir, abcBySku]);

  // The VelocityBar scales against the max velocity in the filtered/sorted set
  // so the bars read proportionally across the whole report.
  const maxVelocity = React.useMemo(
    () => sorted.reduce((max, r) => (r.velocity > max ? r.velocity : max), 0),
    [sorted],
  );

  // Row virtualization (DESIGN.md §14). The table owns its own overflow viewport
  // (matching the BlankConsumptionCard precedent in this feature) so `sticky`
  // header + smooth scrolling stay self-contained regardless of the surrounding
  // dashboard chrome. Rows are real <tr>s in normal table flow; the virtualizer
  // brackets them with a top/bottom spacer row so column alignment with the
  // sticky header is preserved. Dynamic measurement handles the taller two-line
  // rows (SKU + attribute subline).
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 12,
    // Key the measurement cache by row identity, not index — otherwise cached
    // heights briefly attach to the wrong rows after a re-sort/filter.
    getItemKey: (i) => sorted[i]?.skuId ?? sorted[i]?.sku ?? i,
  });
  // Jump back to the top whenever the row set changes (filter/sort/search) so
  // a deep scroll position doesn't strand the viewport past a shrunken set.
  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [sorted]);
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? totalSize)
      : 0;

  // KPIs — computed over the full row set (the period truth), not the filtered view.
  const kpis = React.useMemo(() => {
    const totalUnits = rows.reduce((sum, r) => sum + r.units, 0);
    const skuCount = rows.length;
    const top = rows.length > 0 ? rows[0] : null; // pre-sorted velocity desc
    const atRisk = rows.filter(
      (r) => r.daysOfCover != null && r.daysOfCover < 7,
    ).length;
    const totalOnHand = rows.reduce((sum, r) => sum + r.onHand, 0);
    const preprinted = summarizePreprintedConsumption(rows);
    return {
      totalUnits,
      skuCount,
      top,
      atRisk,
      totalOnHand,
      preprintedUnits: preprinted.units,
      preprintedSkuCount: preprinted.skuCount,
      preprintedShare: preprinted.share,
    };
  }, [rows]);

  const handleSort = (field: SortField) => {
    const nextDirection = sortField === field
      ? sortDir === 'asc' ? 'desc' : 'asc'
      : field === 'sku' ? 'asc' : 'desc';
    setSearchParams(
      (previous) => {
        return updateSearchParams(previous, {
          sort: field === 'velocity' ? null : field,
          dir: nextDirection === 'desc' ? null : nextDirection,
        });
      },
      { replace: true },
    );
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" aria-hidden="true" />;
    }
    return sortDir === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" aria-hidden="true" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" aria-hidden="true" />
    );
  };

  const SortHeader: React.FC<{
    field: SortField;
    label: string;
    align?: 'left' | 'right' | 'center';
  }> = ({ field, label, align = 'right' }) => (
    <th
      className={cn(
        'whitespace-nowrap px-3 py-2 font-medium',
        align === 'right' && 'text-right',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
      )}
    >
      <button
        type="button"
        onClick={() => handleSort(field)}
        aria-sort={
          sortField === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
        }
        className={cn(
          'inline-flex items-center rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        {label}
        <SortIcon field={field} />
      </button>
    </th>
  );

  const isFiltered =
    query.trim() !== '' ||
    category !== ALL_CATEGORIES ||
    typeFilter !== ALL_TYPES ||
    trendFilter !== ALL_TRENDS ||
    stockKind !== ALL_STOCK;
  const showSkeleton = velocity.isLoading && rows.length === 0;
  const reportUnavailable = velocity.isError;
  const unavailableValue = t('reporting.v2.unavailable', 'Unavailable');
  const unavailableDetail = t(
    'reporting.v2.unavailableDetail',
    'Report data could not be loaded',
  );

  const handleExport = () => {
    const csv = buildConsumptionCsv(sorted, abcBySku, {
      startDate,
      endDate,
      locationId,
      locationName,
    });
    downloadCsv(csv, consumptionExportFilename(startDate, endDate));
  };

  const sortLabel = ((): string => {
    switch (sortField) {
      case 'velocity':
        return sortDir === 'desc'
          ? t('reporting.v2.fastestMoving', 'Fastest moving')
          : t('reporting.v2.velocityPerDay', 'Velocity / day');
      case 'sku':
        return t('reporting.v2.sku', 'SKU');
      case 'units':
        return t('reporting.v2.netUnits', 'Net units');
      case 'orders':
        return t('reporting.v2.fulfilledOrders', 'Fulfilled orders');
      case 'trend':
        return t('reporting.v2.momentum', 'Momentum');
      case 'daysOfCover':
        return t('reporting.v2.cover', 'Days of cover');
      case 'onHand':
        return t('reporting.v2.onHand', 'On hand');
      case 'abc':
        return t('reporting.v2.abc', 'ABC');
      default:
        return '';
    }
  })();

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 2xl:grid-cols-6">
        <ScoreTile
          label={t('reporting.v2.netUnitsConsumed', 'Net units consumed')}
          value={reportUnavailable ? unavailableValue : formatUnits(kpis.totalUnits)}
          icon={Boxes}
          loading={showSkeleton}
          detail={reportUnavailable ? unavailableDetail : t('reporting.v2.inThisPeriod', 'In this period')}
        />
        <ScoreTile
          label={t('reporting.v2.skusConsumed', 'SKUs consumed')}
          value={reportUnavailable ? unavailableValue : formatUnits(kpis.skuCount)}
          icon={Package}
          loading={showSkeleton}
          detail={
            reportUnavailable
              ? unavailableDetail
              : t('reporting.v2.distinctConsumedSkus', 'Distinct SKUs with net consumption')
          }
        />
        <ScoreTile
          label={t('reporting.v2.preprintedUsed', 'Pre-printed used')}
          value={reportUnavailable ? unavailableValue : formatUnits(kpis.preprintedUnits)}
          icon={PackageCheck}
          loading={showSkeleton}
          detail={
            reportUnavailable
              ? unavailableDetail
              : t(
                  'reporting.v2.preprintedUsedDetail',
                  '{{skus}} SKUs · {{share}} of net use',
                  {
                    skus: formatUnits(kpis.preprintedSkuCount),
                    share: formatPercent(kpis.preprintedShare),
                  },
                )
          }
        />
        <ScoreTile
          label={t('reporting.v2.topVelocity', 'Top velocity')}
          value={reportUnavailable ? unavailableValue : `${formatRateAdaptive(kpis.top?.velocity ?? 0)}/d`}
          icon={Gauge}
          loading={showSkeleton}
          detail={reportUnavailable ? unavailableDetail : kpis.top?.sku}
        />
        <ScoreTile
          label={t('reporting.v2.skusAtRisk', 'SKUs at risk')}
          value={reportUnavailable ? unavailableValue : formatUnits(kpis.atRisk)}
          icon={Layers}
          loading={showSkeleton}
          band="bad"
          bandLabel={t('reporting.v2.coverUnder7', '< 7d cover')}
          detail={
            reportUnavailable
              ? unavailableDetail
              : t('reporting.v2.runningLow', 'Running low against velocity')
          }
        />
        <ScoreTile
          label={t('reporting.v2.shownOnHand', 'On hand (shown)')}
          value={reportUnavailable ? unavailableValue : formatUnits(kpis.totalOnHand)}
          icon={Boxes}
          loading={showSkeleton}
          detail={
            reportUnavailable
              ? unavailableDetail
              : locationId == null
              ? t('reporting.v2.pooledAllLocations', 'Pooled, all locations')
              : t('reporting.v2.selectedLocation', 'Selected location only')
          }
        />
      </div>

      <div
        className="flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2.5 text-xs text-blue-800 ring-1 ring-blue-200/80"
        role="note"
      >
        <Database className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <p>
          {t(
            'reporting.v2.consumptionBasis',
            'Based on net fulfilled inventory movements. Returns and sync corrections offset consumption; on-hand and velocity use the same location scope.',
          )}
        </p>
      </div>

      {/* Dice controls */}
      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="relative flex-1 lg:min-w-64">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(e) => setFilterParam('q', e.target.value, '')}
            placeholder={t('reporting.v2.searchSkus', 'Search SKU, type, colour or size')}
            className="pl-9"
            aria-label={t('reporting.v2.searchSkus', 'Search SKU, type, colour or size')}
          />
        </div>
        {categories.length > 0 && (
          <Select value={category} onValueChange={(value) => setFilterParam('category', value, ALL_CATEGORIES)}>
            <SelectTrigger
              className="lg:w-48"
              aria-label={t('reporting.v2.filterCategory', 'Filter by category')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>
                {t('reporting.v2.allCategories', 'All categories')}
              </SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {types.length > 0 && (
          <Select value={typeFilter} onValueChange={(value) => setFilterParam('type', value, ALL_TYPES)}>
            <SelectTrigger
              className="lg:w-40"
              aria-label={t('reporting.v2.filterType', 'Filter by type')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>
                {t('reporting.v2.allTypes', 'All types')}
              </SelectItem>
              {types.map((typ) => (
                <SelectItem key={typ} value={typ}>
                  {typ}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select
          value={stockKind}
          onValueChange={(value) => setFilterParam('stock', value, ALL_STOCK)}
        >
          <SelectTrigger
            className="lg:w-44"
            aria-label={t('reporting.v2.filterStockKind', 'Filter by stock kind')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STOCK}>
              {t('reporting.v2.allStock', 'All inventory')}
            </SelectItem>
            <SelectItem value={PREPRINTED_STOCK}>
              {t('reporting.v2.preprintedOnly', 'Pre-printed only')}
            </SelectItem>
            <SelectItem value={OTHER_STOCK}>
              {t('reporting.v2.otherStock', 'Other inventory')}
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={trendFilter} onValueChange={(value) => setFilterParam('trend', value, ALL_TRENDS)}>
          <SelectTrigger
            className="lg:w-36"
            aria-label={t('reporting.v2.filterTrend', 'Filter by trend')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TRENDS}>{t('reporting.v2.allTrends', 'All trends')}</SelectItem>
            <SelectItem value="rising">{t('reporting.v2.rising', 'Rising')}</SelectItem>
            <SelectItem value="falling">{t('reporting.v2.falling', 'Falling')}</SelectItem>
            <SelectItem value="flat">{t('reporting.v2.flat', 'Flat')}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          className="shrink-0 gap-2"
          onClick={handleExport}
          disabled={sorted.length === 0 || velocity.isLoading || velocity.isError}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {t('reporting.v2.exportCsv', 'Export CSV')}
          {sorted.length > 0 && (
            <span className="tabular-nums text-muted-foreground">({sorted.length})</span>
          )}
        </Button>
      </div>

      {/* Row count + active sort */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="tabular-nums">
          {t('reporting.v2.showingNofM', 'Showing {{shown}} of {{total}}', {
            shown: sorted.length,
            total: rows.length,
          })}
        </span>
        <span className="inline-flex items-center gap-1">
          {t('reporting.v2.sortedBy', 'Sorted by')}{' '}
          <span className="font-medium text-foreground">{sortLabel}</span>
        </span>
      </div>

      {/* Flagship table — table-owned scroll viewport with virtualized rows.
          The sticky header pins to this container's top; rows render as real
          <tr>s bracketed by spacer rows so column alignment holds. */}
      <div
        ref={scrollRef}
        role="region"
        aria-label={t('reporting.v2.skuVelocityTable', 'SKU velocity table')}
        tabIndex={0}
        className="max-h-[calc(100dvh-18rem)] overflow-auto rounded-lg border bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <table className="w-full min-w-[64rem] table-fixed border-collapse text-sm">
          <colgroup>
            <col className="w-[18rem]" />
            <col className="w-[6rem]" />
            <col className="w-[9rem]" />
            <col className="w-[7rem]" />
            <col className="w-[6rem]" />
            <col className="w-[8rem]" />
            <col className="w-[6.5rem]" />
            <col className="w-[5rem]" />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground">
            <tr className="border-b">
              <SortHeader field="sku" label={t('reporting.v2.sku', 'SKU')} align="left" />
              <SortHeader field="units" label={t('reporting.v2.netUnits', 'Net units')} />
              <SortHeader
                field="velocity"
                label={t('reporting.v2.velocityPerDayShort', 'Velocity /day')}
              />
              <SortHeader field="trend" label={t('reporting.v2.trend', 'Trend')} />
              <SortHeader
                field="orders"
                label={t('reporting.v2.fulfilledOrdersShort', 'Fulfilled')}
              />
              <SortHeader field="daysOfCover" label={t('reporting.v2.cover', 'Days of cover')} />
              <SortHeader field="onHand" label={t('reporting.v2.onHand', 'On hand')} />
              <SortHeader field="abc" label={t('reporting.v2.abc', 'ABC')} align="center" />
            </tr>
          </thead>
          <tbody>
            {showSkeleton ? (
              Array.from({ length: 12 }).map((_, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td colSpan={8} className="px-3 py-2.5">
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ))
            ) : velocity.isError ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                  {velocity.error?.data?.code === 'FORBIDDEN' &&
                  velocity.error?.message?.includes('Location is required')
                    ? t(
                        'reporting.v2.velocityLocationRequired',
                        'Select a specific location in the top bar to view this report.',
                      )
                    : t(
                        'reporting.v2.velocityError',
                        'Could not load consumption data. Try again shortly.',
                      )}
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                  {isFiltered
                    ? t('reporting.v2.noSkuMatch', 'No SKUs match your search or filters.')
                    : t(
                        'reporting.v2.noSkuConsumption',
                        'No fulfilled inventory consumption was recorded in this period.',
                      )}
                </td>
              </tr>
            ) : (
              <>
                {paddingTop > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={8} style={{ height: paddingTop }} />
                  </tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const row = sorted[virtualRow.index];
                  if (!row) return null;
                  const band = daysOfCoverBand(row.daysOfCover);
                  const { Icon: TrendIcon, cls: trendCls } = TREND_META[row.trend];
                  const abc = abcBySku.get(row.sku) ?? 'C';
                  const attrs = [row.type, row.color, row.size].filter(Boolean).join(' · ');
                  const deltaLabel =
                    row.deltaPct == null
                      ? row.trend === 'rising'
                        ? t('reporting.v2.new', 'new')
                        : '-'
                      : `${row.deltaPct > 0 ? '+' : ''}${formatPercent(row.deltaPct)}`;
                  return (
                    <tr
                      key={row.skuId ?? row.sku}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      className="border-b last:border-0 hover:bg-muted/40 motion-reduce:hover:bg-muted/40"
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate font-mono text-xs font-medium">{row.sku}</p>
                          {row.isPreprinted && (
                            <span className="shrink-0 rounded-full bg-threadheads-purple/10 px-2 py-0.5 text-[10px] font-semibold text-threadheads-purple ring-1 ring-threadheads-purple/25">
                              {t('reporting.v2.preprinted', 'Pre-printed')}
                            </span>
                          )}
                        </div>
                        {attrs && (
                          <p className="truncate text-xs text-muted-foreground">{attrs}</p>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {formatUnits(row.units)}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-2">
                          <span className="tabular-nums">{formatRateAdaptive(row.velocity)}</span>
                          <div className="w-16 shrink-0">
                            <VelocityBar value={row.velocity} max={maxVelocity} />
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={cn(
                            'inline-flex items-center justify-start gap-1 tabular-nums',
                            trendCls,
                          )}
                        >
                          <TrendIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          {deltaLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                        {formatUnits(row.orders)}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2.5 text-right font-semibold tabular-nums',
                          RAG_TEXT[band],
                        )}
                      >
                        {formatExactDaysOfCover(row.daysOfCover)}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2.5 text-right tabular-nums',
                          row.onHand < 0 && 'text-red-600',
                        )}
                      >
                        {formatUnits(row.onHand)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span
                          className={cn(
                            'inline-flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-semibold',
                            ABC_BADGE[abc],
                          )}
                        >
                          {abc}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {paddingBottom > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={8} style={{ height: paddingBottom }} />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
